/**
 * Il programma di un giorno come tabella: le aule in riga, i turni in
 * colonna. Una sessione lunga (Aula Parchi, turni 1–4) occupa piu' colonne
 * invece di ripetersi in ogni turno.
 */

import type { AulaEvento, ProgrammaEvento, SessioneEvento } from '@eventi/tipi.ts';

/** Una sessione messa nella tabella: da quale colonna parte (0 = primo turno) e quante ne occupa. */
export interface CellaGriglia {
  sessione: SessioneEvento;
  colonna: number;
  colonne: number;
}

/**
 * Un'aula nella tabella. Di solito ha una corsia sola; ne ha di piu' solo se
 * due sue sessioni si sovrappongono nello stesso turno (un errore del file,
 * ma meglio vederle tutte e due che una sopra l'altra).
 */
export interface RigaGriglia {
  aula: AulaEvento;
  corsie: CellaGriglia[][];
}

const siSovrappone = (a: CellaGriglia, b: CellaGriglia) =>
  a.colonna < b.colonna + b.colonne && b.colonna < a.colonna + a.colonne;

/**
 * Le righe della tabella di un giorno, nell'ordine delle aule del programma.
 * Entrano solo le sessioni che stanno in almeno un turno e che passano il
 * filtro (es. solo quelle da scegliere); le aule senza sessioni restano fuori.
 */
export function grigliaDelGiorno(
  programma: ProgrammaEvento,
  giorno: string,
  includi: (s: SessioneEvento) => boolean = () => true,
): RigaGriglia[] {
  const posizione = new Map(programma.turni.map((t, i) => [t.n, i]));
  const perAula = new Map<string, CellaGriglia[]>();

  for (const s of programma.sessioni) {
    if (s.giorno !== giorno || !includi(s)) continue;
    const colonne = s.turni.map((n) => posizione.get(n)).filter((i): i is number => i !== undefined);
    if (colonne.length === 0) continue;
    const prima = Math.min(...colonne);
    const cella = { sessione: s, colonna: prima, colonne: Math.max(...colonne) - prima + 1 };
    perAula.set(s.aula, [...(perAula.get(s.aula) ?? []), cella]);
  }

  // Le aule che il file nomina ma non elenca vanno in fondo, con il codice come nome.
  const aule = [
    ...programma.aule,
    ...[...perAula.keys()]
      .filter((codice) => !programma.aule.some((a) => a.codice === codice))
      .map((codice) => ({ codice, nome: codice, posti: null })),
  ];

  return aule.flatMap((aula) => {
    const celle = perAula.get(aula.codice);
    if (!celle) return [];
    const corsie: CellaGriglia[][] = [];
    for (const cella of [...celle].sort((a, b) => a.colonna - b.colonna)) {
      const libera = corsie.find((c) => !c.some((altra) => siSovrappone(altra, cella)));
      if (libera) libera.push(cella);
      else corsie.push([cella]);
    }
    return [{ aula, corsie }];
  });
}

/** Le sessioni del giorno fuori dai turni (la plenaria del mattino), in ordine di orario. */
export function fuoriDaiTurni(programma: ProgrammaEvento, giorno: string): SessioneEvento[] {
  return programma.sessioni
    .filter((s) => s.giorno === giorno && s.turni.length === 0)
    .sort((a, b) => a.inizio.localeCompare(b.inizio));
}

/** Quanti incontri da scegliere ha un'aula in tutto il programma. */
export function incontriDaScegliere(programma: ProgrammaEvento, codiceAula: string): number {
  return programma.sessioni.filter((s) => s.aula === codiceAula && s.sceglibile).length;
}
